import type {
  DungeonCell,
  DungeonDirection,
  DungeonEncounterKind,
} from "./dungeon";

export const DUNGEON_SCENE_MANIFEST_VERSION = 3 as const;
export const DUNGEON_SCENE_CAMERA_RIG = "vault-rig-v1" as const;

export type DungeonHeading = DungeonDirection;
export type DungeonRelativeExit = "forward" | "right" | "back" | "left";
export type DungeonTopology =
  | "dead-end"
  | "straight"
  | "corner"
  | "junction"
  | "cross";
export type DungeonEnvironmentFamily =
  | "ember-crypt"
  | "flooded-reliquary"
  | "echo-vault";

export interface DungeonRouteAnchor {
  slot: DungeonRelativeExit;
  x: number;
  y: number;
  scale: number;
  align: "center" | "left" | "right";
}

export interface DungeonSceneComposition {
  id: string;
  manifestVersion: number;
  cameraRig: string;
  family: DungeonEnvironmentFamily;
  topology: DungeonTopology;
  variant: 1 | 2;
  backgroundPath: string;
  mobileBackgroundPath: string;
  lightingKey: "ember" | "flooded" | "echo";
  materialKey: "basalt" | "wet-stone" | "ceremonial-stone";
  seamProfile: "vault-rig-v1";
  anchorSetId: "vault-anchors-v1";
  fallbackCompositionId: "echo-vault:dead-end:v1";
}

export interface DungeonSceneRoute {
  direction: DungeonDirection;
  relative: DungeonRelativeExit;
  anchor: DungeonRouteAnchor;
}

export interface DungeonScenePlan {
  manifestVersion: number;
  roomIdentity: string;
  viewIdentity: string;
  compositionId: string;
  family: DungeonEnvironmentFamily;
  topology: DungeonTopology;
  variant: 1 | 2;
  accent: 1 | 2 | 3 | 4;
  heading: DungeonHeading;
  backgroundPath: string;
  mobileBackgroundPath: string;
  routes: DungeonSceneRoute[];
}

const DIRECTION_ORDER: readonly DungeonDirection[] = [
  "north",
  "east",
  "south",
  "west",
];

const RELATIVE_ORDER: readonly DungeonRelativeExit[] = [
  "forward",
  "right",
  "back",
  "left",
];

const TOPOLOGIES: readonly DungeonTopology[] = [
  "dead-end",
  "straight",
  "corner",
  "junction",
  "cross",
];

const ENVIRONMENT_FAMILIES: readonly DungeonEnvironmentFamily[] = [
  "ember-crypt",
  "flooded-reliquary",
  "echo-vault",
];

const FAMILY_ASSETS: Record<
  DungeonEnvironmentFamily,
  Pick<
    DungeonSceneComposition,
    | "backgroundPath"
    | "mobileBackgroundPath"
    | "lightingKey"
    | "materialKey"
  >
> = {
  "ember-crypt": {
    backgroundPath: "/assets/game/dungeon-ember-crypt-chamber-v2.webp",
    mobileBackgroundPath:
      "/assets/game/dungeon-ember-crypt-chamber-mobile-v2.webp",
    lightingKey: "ember",
    materialKey: "basalt",
  },
  "flooded-reliquary": {
    backgroundPath:
      "/assets/game/dungeon-flooded-reliquary-chamber-v2.webp",
    mobileBackgroundPath:
      "/assets/game/dungeon-flooded-reliquary-chamber-mobile-v2.webp",
    lightingKey: "flooded",
    materialKey: "wet-stone",
  },
  "echo-vault": {
    backgroundPath: "/assets/game/dungeon-echo-vault-chamber-v2.webp",
    mobileBackgroundPath:
      "/assets/game/dungeon-echo-vault-chamber-mobile-v2.webp",
    lightingKey: "echo",
    materialKey: "ceremonial-stone",
  },
};

export const DUNGEON_ROUTE_ANCHORS: Readonly<
  Record<DungeonRelativeExit, DungeonRouteAnchor>
> = Object.freeze({
  forward: Object.freeze({
    slot: "forward",
    x: 50,
    y: 39,
    scale: 0.94,
    align: "center",
  }),
  right: Object.freeze({
    slot: "right",
    x: 84,
    y: 38,
    scale: 0.9,
    align: "right",
  }),
  back: Object.freeze({
    slot: "back",
    x: 78,
    y: 78,
    scale: 0.86,
    align: "right",
  }),
  left: Object.freeze({
    slot: "left",
    x: 13,
    y: 38,
    scale: 0.9,
    align: "left",
  }),
});

export const DUNGEON_SCENE_COMPOSITIONS: readonly DungeonSceneComposition[] =
  Object.freeze(
    ENVIRONMENT_FAMILIES.flatMap((family) =>
      TOPOLOGIES.flatMap((topology) =>
        ([1, 2] as const).map((variant) => ({
          id: `${family}:${topology}:v${variant}`,
          manifestVersion: DUNGEON_SCENE_MANIFEST_VERSION,
          cameraRig: DUNGEON_SCENE_CAMERA_RIG,
          family,
          topology,
          variant,
          ...FAMILY_ASSETS[family],
          seamProfile: "vault-rig-v1" as const,
          anchorSetId: "vault-anchors-v1" as const,
          fallbackCompositionId: "echo-vault:dead-end:v1" as const,
        })),
      ),
    ),
  );

function hashText(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function directionIndex(direction: DungeonDirection): number {
  return DIRECTION_ORDER.indexOf(direction);
}

export function getDungeonRelativeExit(
  heading: DungeonHeading,
  direction: DungeonDirection,
): DungeonRelativeExit {
  const offset =
    (directionIndex(direction) - directionIndex(heading) + 4) % 4;
  return RELATIVE_ORDER[offset] ?? "forward";
}

export function getDungeonAbsoluteDirection(
  heading: DungeonHeading,
  relative: DungeonRelativeExit,
): DungeonDirection {
  const direction =
    DIRECTION_ORDER[
      (directionIndex(heading) + RELATIVE_ORDER.indexOf(relative) + 4) % 4
    ];
  return direction ?? heading;
}

export function getDungeonInitialHeading(cell: DungeonCell): DungeonHeading {
  return (
    DIRECTION_ORDER.find((direction) => cell.exits.includes(direction)) ??
    "north"
  );
}

export function getDungeonRelativeExits(
  exits: readonly DungeonDirection[],
  heading: DungeonHeading,
): DungeonRelativeExit[] {
  return exits
    .map((direction) => getDungeonRelativeExit(heading, direction))
    .sort(
      (left, right) =>
        RELATIVE_ORDER.indexOf(left) - RELATIVE_ORDER.indexOf(right),
    );
}

export function getDungeonTopology(
  exits: readonly DungeonDirection[],
  heading: DungeonHeading,
): DungeonTopology {
  const relative = new Set(getDungeonRelativeExits(exits, heading));
  if (relative.size <= 1) return "dead-end";
  if (relative.size === 4) return "cross";
  if (relative.size === 3) return "junction";
  const straight =
    (relative.has("forward") && relative.has("back")) ||
    (relative.has("left") && relative.has("right"));
  return straight ? "straight" : "corner";
}

function selectEnvironmentFamily(
  cell: DungeonCell,
  runSeed: number,
): DungeonEnvironmentFamily {
  if (cell.encounterKind === "entrance") return "ember-crypt";
  if (cell.encounterKind === "boss") return "echo-vault";
  const seedOffset = Math.abs(runSeed) % ENVIRONMENT_FAMILIES.length;
  const index =
    (cell.x + cell.y * 2 + seedOffset) % ENVIRONMENT_FAMILIES.length;
  return ENVIRONMENT_FAMILIES[index] ?? "echo-vault";
}

export function createDungeonScenePlan(
  cell: DungeonCell,
  heading: DungeonHeading,
  runSeed: number,
): DungeonScenePlan {
  const family = selectEnvironmentFamily(cell, runSeed);
  const topology = getDungeonTopology(cell.exits, heading);
  const roomHash = hashText(
    `${runSeed}:${cell.id}:${DUNGEON_SCENE_MANIFEST_VERSION}`,
  );
  const viewHash = hashText(`${roomHash}:${heading}:${topology}`);
  const variant = ((viewHash % 2) + 1) as 1 | 2;
  const accent = ((Math.floor(viewHash / 2) % 4) + 1) as 1 | 2 | 3 | 4;
  const compositionId = `${family}:${topology}:v${variant}`;
  const composition =
    DUNGEON_SCENE_COMPOSITIONS.find(
      (candidate) => candidate.id === compositionId,
    ) ?? DUNGEON_SCENE_COMPOSITIONS[0];

  if (!composition) {
    throw new Error("Dungeon scene catalog has no fallback composition.");
  }

  const routes = cell.exits.map((direction) => {
    const relative = getDungeonRelativeExit(heading, direction);
    return {
      direction,
      relative,
      anchor: DUNGEON_ROUTE_ANCHORS[relative],
    };
  });

  return {
    manifestVersion: DUNGEON_SCENE_MANIFEST_VERSION,
    roomIdentity: `${runSeed}:${cell.id}:${family}`,
    viewIdentity: `${runSeed}:${cell.id}:${heading}:${topology}:a${accent}`,
    compositionId: composition.id,
    family: composition.family,
    topology: composition.topology,
    variant,
    accent,
    heading,
    backgroundPath: composition.backgroundPath,
    mobileBackgroundPath: composition.mobileBackgroundPath,
    routes,
  };
}

export interface DungeonSceneValidationResult {
  valid: boolean;
  errors: string[];
}

export function validateDungeonSceneCatalog(
  catalog: readonly DungeonSceneComposition[] = DUNGEON_SCENE_COMPOSITIONS,
): DungeonSceneValidationResult {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const composition of catalog) {
    if (ids.has(composition.id)) {
      errors.push(`Duplicate dungeon scene composition: ${composition.id}`);
    }
    ids.add(composition.id);
    if (composition.manifestVersion !== DUNGEON_SCENE_MANIFEST_VERSION) {
      errors.push(`Manifest version mismatch: ${composition.id}`);
    }
    if (composition.cameraRig !== DUNGEON_SCENE_CAMERA_RIG) {
      errors.push(`Camera rig mismatch: ${composition.id}`);
    }
    if (!composition.backgroundPath || !composition.mobileBackgroundPath) {
      errors.push(`Missing environment asset: ${composition.id}`);
    }
    if (!TOPOLOGIES.includes(composition.topology)) {
      errors.push(`Unknown topology: ${composition.id}`);
    }
  }
  for (const family of ENVIRONMENT_FAMILIES) {
    for (const topology of TOPOLOGIES) {
      if (
        !catalog.some(
          (composition) =>
            composition.family === family &&
            composition.topology === topology,
        )
      ) {
        errors.push(`Missing ${family} ${topology} composition.`);
      }
    }
  }
  for (const relative of RELATIVE_ORDER) {
    const anchor = DUNGEON_ROUTE_ANCHORS[relative];
    if (
      !anchor ||
      anchor.x < 0 ||
      anchor.x > 100 ||
      anchor.y < 0 ||
      anchor.y > 100
    ) {
      errors.push(`Invalid ${relative} route anchor.`);
    }
  }
  return { valid: errors.length === 0, errors };
}

export function isDungeonEncounterKind(
  value: unknown,
): value is DungeonEncounterKind {
  return ["entrance", "monster", "trap", "cache", "boss"].includes(
    String(value),
  );
}
